import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, IconButton,
  Chip, Stack, Avatar, Paper
} from "@mui/material";
import SendIcon from '@mui/icons-material/Send';
import { keyframes } from "@emotion/react";
import BotAvatar from '../../../assets/images/avatar/bot_avtr.jpg';
import UserAvatar from '../../../assets/images/avatar/human_agent.png';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axiosInstance from "../../../helpers/axios";

const suggestionQuestions = [
  "What is the credit risk of this applicant?",
  "What feature most influenced the decision?"
];

const typingAnimation = keyframes`
  0% { opacity: 0.2; }
  20% { opacity: 1; }
  100% { opacity: 0.2; }
`;

const TypingDots = () => (
  <Box sx={{ display: 'flex', gap: 0.5 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 6,
          height: 6,
          bgcolor: 'text.primary',
          borderRadius: '50%',
          animation: `${typingAnimation} 1.2s ease-in-out ${i * 0.2}s infinite`
        }}
      />
    ))}
  </Box>
);

export default function MonkCibilAi({ applicant_id, applicationId, shouldFetchData = true}) {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatAvailable,setChatAvailable] = useState(false);
  const [chatHistory,setChatHistory] = useState(null)
  const user = null
  const monkAiUrl = `/chat?userId=${applicant_id}&applicationId=${applicationId}`

  useEffect(() => {
     
      const controller = new AbortController();
    
      const fetchData = async () => {
        if (!shouldFetchData) return;  
        
        try {
          const [monkAiResponse] = await Promise.all([
            axiosInstance.get(monkAiUrl, {
              signal: controller.signal,
            }),
          
          ]);
          setChatHistory(monkAiResponse?.data?.data?.chatHistory);
    
        } catch (err) {
          if (err.name !== 'CanceledError') {
            console.error("Failed to fetch data:", err);
          }
        } finally {
        }
      };
    
      fetchData();
    
      return () => {
        controller.abort();
      };
      
    }, [user,monkAiUrl, shouldFetchData]);


  useEffect(() => {

    if (chatHistory && Array.isArray(chatHistory)) {
      setChatAvailable(true)
      const initialMessages = normalizedMessages(chatHistory)
      // const initialMessages = chatHistory.map((item) => ({
      //   sender: item.role || 'bot',
      //   text: item.content,
      //   borderColor: (item?.content?.approval_probability > 0.8? 'green': item?.content?.approval_probability > 0.6? 'orange': 'red')
      // }));     
      setMessages(initialMessages);
    }
    else {
      setMessages([{sender: "bot",type: 'text', text: "No Analysis Yet"}]);
    }
  }, [chatHistory]);

  function normalizedMessages(chatHistory) {
    return chatHistory?.map(entry => {
    const { role, timestamp, content } = entry;
  
    if (role === 'bot' && content?.pros && content?.cons) {
      return {
        sender: 'bot',
        type: 'structured',
        pros: content?.pros,
        cons: content?.cons,
        summary: content?.summary,
        approvalProbability: content?.approval_probability,
        timestamp,
      };
    } else {
      return {
        sender: role,
        type: 'text',
        text: content?.message || content?.response || '',
        timestamp,
      };
    }
  });
  }

  const handleChipClick = (question) => {
    setInput(question);
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { sender: "user", text: input };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/chat/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // body: JSON.stringify({ message: input, applicant_id: applicant_id, chat_history: [] })
        body: JSON.stringify({userId: applicant_id, applicationId, message: input})
      });
      const data = await res.json(); // should get entire array of chat history
      // const updatedMessages = data?.chatHistory?.map((msg) => ({
      //   sender: msg?.role,
      //   text: msg.content
      // })) || [];

      const updatedMessages = normalizedMessages(data?.data?.chatHistory)

      setMessages(updatedMessages);
      // const risk = data[0]?.content?.approval_probability;
      
      // if (typeof risk === "number") {
      //   if (risk > 0.9) borderColor = "green";
      //   else if (risk > 0.5) borderColor = "orange";
      //   else borderColor = "red";
      // }
      // // const botMessage = { sender: "bot", text: data || "No answer found.", borderColor: borderColor };
      // const botMessage = {sender: data[0]?.chatHistory[0]?.role, text: data[0]?.chatHistory[0]?.content}
      // setMessages(prev => [...prev, botMessage]);
      // 
    } catch (err) {
      setMessages(prev => [...prev, { sender: "bot", text: "Error getting response from API." }]);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box sx={{ padding: 4, maxWidth: 1500, margin: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={300}>
            Credit Risk Assessment Overview
          </Typography>
          {/* <Typography variant="subtitle2" color="text.secondary">
            Source: Cibil Report
          </Typography> */}
        </Box>
      </Stack>

      {/* <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body1">
            
          </Typography>
        </CardContent>
      </Card> */}


      {/* Chat Messages */}
      <Stack spacing={2} mb={2}>
        {messages?.map((msg, index) => (
          <Stack
            key={index}
            direction="row"
            justifyContent={msg?.sender === "user" ? "flex-end" : "flex-start"}
          >
            {msg?.sender === "bot" && <Avatar src={BotAvatar} alt="Bot" />}
            <Paper
              sx={{
                p: 1.5,
                bgcolor: msg?.sender === "user" ? "primary.light" : "grey.100",
                maxWidth: "75%",
                border: msg?.type === 'structured' ? `1px solid ${msg?.borderColor || 'blue'}` : undefined,
              }}
              elevation={1}
            >
              {msg?.type === 'structured' ? (
                <>
                  <Typography variant="subtitle1" sx={{fontWeight: "bold"}}>Pros:</Typography>
                  <ul style={{listStyle: "none"}}>
                    {msg?.pros.map((p, i) => (
                      <li key={i}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CheckCircleIcon fontSize="small" sx={{ color: "green" }} />
                          <Typography variant="body2" sx={{ color: "green", fontWeight: "normal" }}>{p}</Typography>
                        </Stack>
                      </li>
                    ))}
                  </ul>

                  <Typography variant="subtitle1" sx={{fontWeight: "bold"}}>Cons:</Typography>
                  <ul style={{listStyle: "none"}}>
                    {msg?.cons.map((c, i) => (
                      <li key={i}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CancelIcon fontSize="small" sx={{ color: "red" }} />
                          <Typography variant="body2" sx={{ color: "red", fontWeight: "normal" }}>{c}</Typography>
                        </Stack>
                      </li>
                    ))}
                  </ul>

                  {msg?.summary && (
                    <>
                      <Typography variant="subtitle1" sx={{fontWeight: "bold"}}>Summary:</Typography>
                      <Typography variant="body2">{msg?.summary}</Typography>
                    </>
                  )}
                </>
              ) : (
                <Typography variant="body2">{msg?.text}</Typography>
              )}
            </Paper>
            {msg?.sender === "user" && <Avatar src={UserAvatar} alt="User" />}
          </Stack>
        ))}

        {loading && (
          <Stack direction="row" justifyContent="flex-start">
            <Avatar src={BotAvatar} alt="Bot" />
            <Paper
              sx={{
                p: 1.5,
                bgcolor: "grey.100",
                maxWidth: "75%",
                display: "flex",
                alignItems: "center",
              }}
              elevation={1}
            >
              <TypingDots />
            </Paper>
          </Stack>
        )}
      </Stack>


      {/* Input Field */}
      {chatAvailable&&<> 
      <Box>
        <Box display="flex" gap={1} alignItems="flex-end">
          <TextField
            fullWidth
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Start typing..."
            variant="outlined"
            multiline
          />
          <IconButton color="primary" onClick={handleSend}>
            <SendIcon />
          </IconButton>
        </Box>

        <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
          {suggestionQuestions.map((q, i) => (
            <Chip key={i} label={q} onClick={() => handleChipClick(q)} clickable size="small" />
          ))}
        </Stack>
      </Box>
      </>}
     
    </Box>
  );
}
